import {isSameMonth, sub} from 'date-fns';
import {RefObject, useCallback, useEffect} from 'react';
import {getReactRootDocument} from 'utils/dom';

import {
    getCalendarRowCount,
    getDateFromId,
    getElementById,
    getFirstEnabledDate,
    getLastEnabledDate,
} from '../utils';

interface OnArrowProps {
    currentNumber: number;
    firstEnabledNumber: number | undefined;
    id: string;
    isNextDisabled: boolean;
    isPreviousDisabled: boolean;
    lastEnabledNumber: number | undefined;
    onNext: () => void;
    onPrevious: () => void;
}

type OnArrowFunction = (props: OnArrowProps) => string | undefined;

const selector = {
    date: (id: string, date: number) => `${id}-date-selector-date-${date}`,
    // nextButton: (id: string) => `${id}-date-selector-nav-next`,
    // previousButton: (id: string) => `${id}-date-selector-nav-previous`,
};

const getNextFocus: Record<string, OnArrowFunction> = {
    ArrowDown: ({currentNumber, id, lastEnabledNumber, onNext}) => {
        const currentDate = getDateFromId(id, currentNumber);
        const nextDate = getDateFromId(id, currentNumber + 7);
        const lastEnabledDate =
            lastEnabledNumber === undefined
                ? null
                : getDateFromId(id, lastEnabledNumber);

        if (!currentDate || !lastEnabledDate) {
            return undefined;
        }

        // If next date is in the same month
        if (nextDate && isSameMonth(currentDate, nextDate))
            return selector.date(id, currentNumber + 7);

        // Go to next month
        onNext();

        const nextNumber =
            nextDate || isSameMonth(currentDate, lastEnabledDate)
                ? currentNumber % 7
                : (currentNumber % 7) + 7;

        return selector.date(id, nextNumber);
    },
    ArrowLeft: ({currentNumber, id, onPrevious}) => {
        const currentDate = getDateFromId(id, currentNumber);
        const previousDate = getDateFromId(id, currentNumber - 1);

        if (!currentDate) {
            return undefined;
        }

        // If previous date is in the same month
        if (previousDate && isSameMonth(currentDate, previousDate))
            return selector.date(id, currentNumber - 1);

        // Go to previous month
        onPrevious();

        const rows = getCalendarRowCount(sub(currentDate, {days: 1}));
        const nextNumber = previousDate
            ? (rows - 1) * 7 + (currentNumber % 7) - 1
            : rows * 7 - 1;

        return selector.date(id, nextNumber);
    },

    ArrowRight: ({currentNumber, id, onNext}) => {
        const currentDate = getDateFromId(id, currentNumber);
        const nextDate = getDateFromId(id, currentNumber + 1);

        if (!currentDate) {
            return undefined;
        }

        // If next date is in the same month
        if (nextDate && isSameMonth(currentDate, nextDate))
            return selector.date(id, currentNumber + 1);

        // Go to next month
        onNext();

        const nextNumber = nextDate ? (currentNumber % 7) + 1 : 0;

        return selector.date(id, nextNumber);
    },
    ArrowUp: ({currentNumber, firstEnabledNumber, id, onPrevious}) => {
        const currentDate = getDateFromId(id, currentNumber);
        const previousDate = getDateFromId(id, currentNumber - 7);
        const firstEnabledDate =
            firstEnabledNumber === undefined
                ? null
                : getDateFromId(id, firstEnabledNumber);

        if (!currentDate || !firstEnabledDate) {
            return undefined;
        }

        // If previous date is in the same month
        if (previousDate && isSameMonth(currentDate, previousDate))
            return selector.date(id, currentNumber - 7);

        // Go to previous month
        onPrevious();

        const rows = getCalendarRowCount(sub(currentDate, {days: 7}));
        const nextNumber =
            previousDate || isSameMonth(currentDate, firstEnabledDate)
                ? (rows - 1) * 7 + (currentNumber % 7)
                : (rows - 2) * 7 + (currentNumber % 7);

        return selector.date(id, nextNumber);
    },
};

interface UseKeyDownCalendarProps {
    currentFocus: string | undefined;
    id: string;
    isNextDisabled: boolean;
    isPreviousDisabled: boolean;
    onNext: () => void;
    onPrevious: () => void;
    onShow: () => void;
    refInput: RefObject<HTMLInputElement>;
    setCurrentFocus: (currentFocus: string | undefined) => void;
}

const useKeyDown = ({
    currentFocus,
    id,
    isNextDisabled,
    isPreviousDisabled,
    onNext,
    onPrevious,
    onShow,
    refInput,
    setCurrentFocus,
}: UseKeyDownCalendarProps) => {
    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (refInput.current === getReactRootDocument()?.activeElement) {
                if (event.key === 'Enter') {
                    onShow();
                }

                return undefined;
            }

            if (event.key === 'Enter' && !!currentFocus) {
                const element = getElementById(currentFocus);
                element?.click();
                event.stopPropagation();
                event.preventDefault();
            }

            if (event.key.includes('Arrow')) {
                event.preventDefault();
                const firstEnabledDate = getFirstEnabledDate(id);
                const lastEnabledDate = getLastEnabledDate(id);

                const firstEnabledNumber = firstEnabledDate
                    ? Number(firstEnabledDate.split('-').pop())
                    : undefined;
                const lastEnabledNumber = lastEnabledDate
                    ? Number(lastEnabledDate.split('-').pop())
                    : undefined;
                const currentNumber = currentFocus?.includes(
                    'date-selector-date',
                )
                    ? Number(currentFocus.split('-').pop())
                    : undefined;

                let nextFocus: string | undefined;

                if (currentFocus?.includes('date-selector-nav')) {
                    nextFocus = firstEnabledDate;
                } else if (currentNumber === undefined) {
                    nextFocus = undefined;
                } else {
                    nextFocus = getNextFocus[event.key]?.({
                        currentNumber,
                        firstEnabledNumber,
                        id,
                        isNextDisabled,
                        isPreviousDisabled,
                        lastEnabledNumber,
                        onNext,
                        onPrevious,
                    });
                }

                if (nextFocus) {
                    setCurrentFocus(nextFocus);
                }
            }
        },
        [
            currentFocus,
            id,
            isNextDisabled,
            isPreviousDisabled,
            onNext,
            onPrevious,
            onShow,
            refInput,
            setCurrentFocus,
        ],
    );

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onKeyDown]);
};

export default useKeyDown;
